import React, { useState, useRef, useCallback } from "react";
import { isEmbroideryTrace } from "../../config/appVariant";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import logo from "../../assets/logo1.svg";
import "./ProWelcomeScreen.css";

const PRO_FEATURES = [
  { icon: "⬇️", text: "Unlimited SVG Exports" },
  { icon: "🧵", text: "Full Access to Embroidery Editor" },
  { icon: "🎨", text: "Commercial License" },
  { icon: "🔄", text: "AI Retrace & Modify" },
  { icon: "✂️", text: "Remove Background" },
  { icon: "📦", text: "Bulk Convert" },
  ...(!isEmbroideryTrace ? [{ icon: "🖼️", text: "AI Image Generation" }] : []),
];

const SLIDES = [
  {
    id: "success",
    title: "You're All Set!",
    subtitle: "Welcome to SvgTrace. All features are now unlocked.",
  },
  {
    id: "features",
    title: "What's Unlocked",
    subtitle: "You now have full access to every Pro feature.",
  },
  {
    id: "start",
    title: "Start Creating",
    subtitle:
      "Upload any image and turn it into clean SVGs or embroidery-ready files.",
  },
];

const SWIPE_THRESHOLD = 50;

const ProWelcomeScreen = ({ isVisible, onDismiss }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goTo = useCallback(
    (idx) => {
      if (idx >= 0 && idx < SLIDES.length) setActiveSlide(idx);
    },
    []
  );

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) < SWIPE_THRESHOLD) return;
    if (diff > 0) {
      setActiveSlide((s) => Math.min(s + 1, SLIDES.length - 1));
    } else {
      setActiveSlide((s) => Math.max(s - 1, 0));
    }
  }, []);

  if (!isVisible) return null;

  const isLast = activeSlide === SLIDES.length - 1;

  const content = (
    <div
      className="pro-welcome"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="pro-welcome__inner">
        {/* --- Slide 0: Success --- */}
        <div
          className={`pro-welcome__slide ${activeSlide === 0 ? "pro-welcome__slide--active" : ""}`}
        >
          <div className="pro-welcome__checkmark">
            <div className="pro-welcome__circle">
              <FontAwesomeIcon icon={faCheck} />
            </div>
          </div>

          <img
            src={logo}
            alt="SvgTrace"
            className="pro-welcome__logo"
          />

          <h2 className="pro-welcome__title">{SLIDES[0].title}</h2>
          <p className="pro-welcome__subtitle">{SLIDES[0].subtitle}</p>
        </div>

        {/* --- Slide 1: Features --- */}
        <div
          className={`pro-welcome__slide ${activeSlide === 1 ? "pro-welcome__slide--active" : ""}`}
        >
          <h2 className="pro-welcome__title">{SLIDES[1].title}</h2>
          <p className="pro-welcome__subtitle">{SLIDES[1].subtitle}</p>

          <div className="pro-welcome__features">
            {PRO_FEATURES.map((f) => (
              <div className="pro-welcome__feature" key={f.text}>
                <span className="pro-welcome__feature-icon">{f.icon}</span>
                <span className="pro-welcome__feature-text">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* --- Slide 2: Get Started --- */}
        <div
          className={`pro-welcome__slide ${activeSlide === 2 ? "pro-welcome__slide--active" : ""}`}
        >
          <div className="pro-welcome__rocket">🚀</div>

          <h2 className="pro-welcome__title">{SLIDES[2].title}</h2>
          <p className="pro-welcome__subtitle">{SLIDES[2].subtitle}</p>

          <button className="pro-welcome__cta" onClick={onDismiss}>
            Start Creating
          </button>
        </div>

        {/* --- Navigation --- */}
        <div className="pro-welcome__nav">
          <div className="pro-welcome__dots">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                className={`pro-welcome__dot ${i === activeSlide ? "pro-welcome__dot--active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {!isLast ? (
            <button
              className="pro-welcome__next"
              onClick={() => goTo(activeSlide + 1)}
            >
              Next
            </button>
          ) : (
            <button className="pro-welcome__next" onClick={onDismiss}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ProWelcomeScreen;
